import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Formatar data sem problemas de timezone
const formatDateSafe = (date) => {
  if (!date) return '';
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  const [year, month, day] = dateStr.split('T')[0].split('-');
  return `${day}/${month}/${year}`;
};

const InactiveGoals = () => {
  const [goals, setGoals] = useState([]);
  const [operations, setOperations] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reactivating, setReactivating] = useState({});
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [filters, setFilters] = useState({
    operation: '',
    unit: '',
    department: '',
    search: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const goalsResponse = await axios.get('/goals', {
        params: { ...filters, type: 'inactive' },
      });
      if (goalsResponse.data.success) {
        setGoals(goalsResponse.data.goals || []);
      }

      const dashboardResponse = await axios.get('/dashboard');
      if (dashboardResponse.data.success) {
        setOperations(dashboardResponse.data.data.operations || []);
        setUnits(dashboardResponse.data.data.units || []);
        setDepartments(dashboardResponse.data.data.departments || []);
      }
    } catch (error) {
      console.error('Error loading inactive goals:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatNumber = (num) => {
    if (!num) return '0,00';
    return parseFloat(num).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleReactivate = async (goalId) => {
    if (!window.confirm('Deseja reativar esta meta e todas as metas relacionadas?')) return;

    setReactivating(prev => ({ ...prev, [goalId]: true }));

    try {
      const response = await axios.post(`/goals/${goalId}/toggle-status`, {
        action: 'reactivate_all',
      });

      if (response.data.success) {
        await loadData();
      }
    } catch (error) {
      console.error('Error reactivating goal:', error);
      alert(error.response?.data?.error || 'Erro ao reativar meta');
    } finally {
      setReactivating(prev => ({ ...prev, [goalId]: false }));
    }
  };

  const getPeriodLabel = (period) => {
    const labels = {
      daily: 'Diária',
      weekly: 'Semanal',
      monthly: 'Mensal',
      quarterly: 'Trimestral',
      annual: 'Anual',
    };
    return labels[period] || period;
  };

  const getPeriodIcon = (period) => {
    const icons = {
      daily: 'fa-calendar-day',
      weekly: 'fa-calendar-week',
      monthly: 'fa-calendar-alt',
      quarterly: 'fa-calendar',
      annual: 'fa-calendar-check',
    };
    return icons[period] || 'fa-calendar';
  };

  const calculateProgress = (goal) => {
    if (goal.progress_percentage && parseFloat(goal.progress_percentage) > 0) {
      return parseFloat(goal.progress_percentage);
    }
    const current = parseFloat(goal.current_value) || 0;
    const target = parseFloat(goal.target_value) || 0;
    if (target === 0) return 0;
    return (current / target) * 100;
  };

  const toggleFamily = (familyKey) => {
    setExpandedFamilies(prev => ({ ...prev, [familyKey]: !prev[familyKey] }));
  };

  // ──────────────────────────────────────────────
  // Agrupar metas por "família" (parent_goal_id)
  // e selecionar a do período mais novo como representante
  // ──────────────────────────────────────────────
  const buildFamilyGroups = () => {
    // 1. Agrupar por família: chave = parent_goal_id || id
    const families = {};
    goals.forEach(goal => {
      const familyKey = String(goal.parent_goal_id || goal.id);
      if (!families[familyKey]) {
        families[familyKey] = [];
      }
      families[familyKey].push(goal);
    });

    // 2. Para cada família, ordenar por start_date DESC e pegar o mais recente
    const representatives = [];
    Object.entries(families).forEach(([familyKey, familyGoals]) => {
      // Ordenar por start_date DESC (mais novo primeiro)
      const sorted = [...familyGoals].sort((a, b) => {
        const dateA = new Date(a.start_date || 0);
        const dateB = new Date(b.start_date || 0);
        return dateB - dateA;
      });

      const newest = sorted[0];
      representatives.push({
        ...newest,
        _familyKey: familyKey,
        _familyGoals: sorted,
        _familyCount: sorted.length,
      });
    });

    return representatives;
  };

  const familyReps = buildFamilyGroups();

  // Ordenar representantes por título
  const sortedReps = [...familyReps].sort((a, b) => {
    return (a.title || '').localeCompare(b.title || '', 'pt-BR');
  });

  // Agrupar por operação e unidade
  const groupedGoals = {};
  sortedReps.forEach(goal => {
    const opKey = goal.operation_name || 'Sem Operação';
    const unitKey = goal.unit_name || 'Sem Unidade';

    if (!groupedGoals[opKey]) {
      groupedGoals[opKey] = {};
    }
    if (!groupedGoals[opKey][unitKey]) {
      groupedGoals[opKey][unitKey] = [];
    }
    groupedGoals[opKey][unitKey].push(goal);
  });

  const totalFamilies = sortedReps.length;
  const totalGoals = goals.length;

  // Renderizar um card de meta (usado tanto para representante quanto para expandidos)
  const renderGoalCard = (goal, isChild = false) => {
    const progressValue = calculateProgress(goal);
    const isReactivating = reactivating[goal.id];

    return (
      <div
        key={goal.id}
        className="goals-card goals-card-compact"
        style={{
          opacity: isChild ? 0.7 : 0.85,
          borderLeft: `3px solid ${isChild ? '#e67e22' : '#f39c12'}`,
          ...(isChild ? { marginLeft: '1rem', fontSize: '0.9em' } : {}),
        }}
      >
        {/* Status tag */}
        <div className="goals-concluded-tag" style={{ background: '#f39c12' }}>
          <i className="fas fa-pause-circle"></i>
          <span>INATIVA</span>
        </div>

        {/* Header: título + porcentagem */}
        <div className="goals-card-header-compact">
          <div className="goals-header-row">
            <Link to={`/goals/${goal.id}`} className="goals-card-title-link">
              <h3 className="goals-card-title-compact">{goal.title}</h3>
            </Link>
            <div className={`goals-progress-percentage-compact ${goal.is_positive_goal === false ? 'negative-goal' : ''}`}>
              {progressValue.toFixed(0)}%
            </div>
          </div>

          {/* Badges */}
          <div className="goals-badges-compact">
            <span className={`goals-badge-mini ${goal.period}`} title={getPeriodLabel(goal.period)}>
              <i className={`fas ${getPeriodIcon(goal.period)}`}></i>
            </span>
            <span className="goals-badge-mini" style={{ background: '#f39c12', color: '#fff' }} title="Inativa">
              <i className="fas fa-pause"></i>
            </span>
            {goal.department_name && (
              <span className="goals-badge-mini dept" title={goal.department_name}>
                <i className="fas fa-building"></i>
              </span>
            )}
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="goals-progress-compact-bar">
          <div className="goals-progress-bar-mini">
            <div
              className={`goals-progress-fill ${goal.is_positive_goal === false ? 'negative-goal' : ''}`}
              style={{ width: `${Math.min(progressValue, 100)}%`, opacity: 0.6 }}
            ></div>
          </div>
          <div className="goals-values-compact">
            <span>{formatNumber(goal.current_value)}</span>
            <span className="goals-separator">/</span>
            <span>{formatNumber(goal.target_value)}</span>
          </div>
        </div>

        {/* Datas */}
        {goal.start_date && (
          <div className="goals-values-compact" style={{ fontSize: '0.7rem', color: '#888', padding: '0 0.5rem', marginTop: '-0.25rem' }}>
            <span>
              <i className="fas fa-calendar-alt" style={{ marginRight: '0.25rem' }}></i>
              {formatDateSafe(goal.start_date)}
              {goal.end_date && ` - ${formatDateSafe(goal.end_date)}`}
            </span>
          </div>
        )}

        {/* Ações */}
        <div className="goals-actions-compact">
          <Link to={`/goals/${goal.id}`} className="goals-action-btn info" title="Detalhes">
            <i className="fas fa-chart-line"></i>
          </Link>
          {!isChild && (
            <button
              className={`goals-action-btn conclude ${isReactivating ? 'loading' : ''}`}
              title="Reativar meta e todas relacionadas"
              onClick={() => handleReactivate(goal.id)}
              disabled={isReactivating}
              style={{ background: '#27ae60', color: '#fff' }}
            >
              <i className={`fas ${isReactivating ? 'fa-spinner fa-spin' : 'fa-play-circle'}`}></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="goals-list-page">
      {/* Header + Filtros */}
      <div className="goals-toolbar">
        <div className="goals-toolbar-row">
          <div className="goals-toolbar-title">
            <h1><i className="fas fa-pause-circle"></i> Metas Inativas</h1>
            <span className="goals-date-badge" style={{ background: '#f39c12', color: '#fff' }}>
              {totalFamilies} {totalFamilies === 1 ? 'grupo' : 'grupos'}
              {totalGoals !== totalFamilies && ` (${totalGoals} metas)`}
            </span>
          </div>

          <div className="goals-toolbar-actions">
            <Link to="/goals" className="goals-toolbar-btn primary" title="Metas Ativas">
              <i className="fas fa-bullseye"></i>
            </Link>
            <Link to="/dashboard" className="goals-toolbar-btn success" title="Dashboard">
              <i className="fas fa-chart-line"></i>
            </Link>
          </div>
        </div>

        {/* Filtros em linha */}
        <div className="goals-toolbar-filters">
          <select
            className="goals-filter-select"
            value={filters.operation}
            onChange={(e) => handleFilterChange('operation', e.target.value)}
            title="Operação"
          >
            <option value="">Operação</option>
            {operations.map(op => (
              <option key={op.id} value={op.code}>{op.name}</option>
            ))}
          </select>

          <select
            className="goals-filter-select"
            value={filters.unit}
            onChange={(e) => handleFilterChange('unit', e.target.value)}
            title="Unidade"
          >
            <option value="">Unidade</option>
            {units.map(unit => (
              <option key={unit.id} value={unit.code}>{unit.name}</option>
            ))}
          </select>

          <select
            className="goals-filter-select"
            value={filters.department}
            onChange={(e) => handleFilterChange('department', e.target.value)}
            title="Departamento"
          >
            <option value="">Depto</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          <div className="goals-filter-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar título ou descrição..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Lista de Metas Inativas Agrupadas */}
      {loading ? (
        <div className="loading">Carregando...</div>
      ) : sortedReps.length === 0 ? (
        <div className="no-goals">
          <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: '#27ae60', marginBottom: '0.5rem' }}></i>
          <p>Nenhuma meta inativa encontrada</p>
          <Link to="/goals" className="goals-toolbar-btn primary" style={{ display: 'inline-flex', marginTop: '1rem' }}>
            <i className="fas fa-arrow-left" style={{ marginRight: '0.5rem' }}></i> Voltar para Metas
          </Link>
        </div>
      ) : (
        <div className="operations-grid">
          {Object.keys(groupedGoals).map(operationName => {
            const operationGoals = groupedGoals[operationName];
            const allRepsInOp = Object.values(operationGoals).flat();
            const totalFamiliesInOp = allRepsInOp.length;
            const totalGoalsInOp = allRepsInOp.reduce((sum, r) => sum + r._familyCount, 0);

            return (
              <section key={operationName} className="period-section operation-section">
                {/* Operation Header */}
                <div className="period-header period-header-bg-goals" style={{ borderLeft: '4px solid #f39c12' }}>
                  <h4 className="period-title">
                    <i className="fas fa-building"></i>
                    {operationName}
                  </h4>
                  <span className="period-count">
                    {totalFamiliesInOp} {totalFamiliesInOp === 1 ? 'grupo' : 'grupos'}
                    {totalGoalsInOp !== totalFamiliesInOp && ` (${totalGoalsInOp} metas)`}
                  </span>
                </div>

                {/* Units */}
                <div className="operation-content">
                  <div className="units-grid">
                    {Object.keys(operationGoals).map(unitName => (
                      <article key={unitName} className="unit-section">
                        <div className="period-header period-header-bg-goals">
                          <h4 className="period-title">
                            <i className="fas fa-industry"></i>
                            {unitName}
                          </h4>
                        </div>
                        <div className="goals-cards-grid">
                          {operationGoals[unitName].map(rep => {
                            const isExpanded = expandedFamilies[rep._familyKey];
                            const olderGoals = rep._familyGoals.slice(1); // todos exceto o mais novo

                            return (
                              <React.Fragment key={rep._familyKey}>
                                {/* Card principal (período mais novo) */}
                                <div style={{ position: 'relative' }}>
                                  {renderGoalCard(rep)}

                                  {/* Botão de expandir se há mais períodos */}
                                  {rep._familyCount > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => toggleFamily(rep._familyKey)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.35rem',
                                        width: '100%',
                                        padding: '0.35rem 0.5rem',
                                        border: '1px dashed #e67e22',
                                        borderTop: 'none',
                                        borderRadius: '0 0 8px 8px',
                                        background: isExpanded ? '#fef5e7' : '#fdf2e9',
                                        color: '#e67e22',
                                        fontSize: '0.72rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                      {isExpanded
                                        ? 'Ocultar períodos anteriores'
                                        : `+${rep._familyCount - 1} ${rep._familyCount - 1 === 1 ? 'período anterior' : 'períodos anteriores'}`
                                      }
                                    </button>
                                  )}
                                </div>

                                {/* Cards dos períodos anteriores (expandido) */}
                                {isExpanded && olderGoals.map(childGoal => (
                                  renderGoalCard(childGoal, true)
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InactiveGoals;
